import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ArrowLeft, Copy, CopyPlus, ExternalLink, Link2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatWeekday } from '../lib/format';
import {
  BOOKING_BUFFERS,
  BOOKING_DAYS_AHEAD,
  BOOKING_DURATIONS,
  BOOKING_NOTICES,
  MAX_BOOKING_INTRO,
  MAX_BOOKING_TITLE,
  MAX_BOOKING_WINDOWS_A_DAY,
  MAX_MEETING_LOCATION,
  MEETING_PLACES,
  bookingDaysText,
  bookingTimeText,
  dayHoursProblems,
  minuteToTime,
  timeToMinute,
  type AvailabilityWindow,
  type BookingPage,
  type MeetingPlace,
} from '../lib/meetings';
import { refusalText } from '../lib/reach';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '../components/ui';
import { useWeekStart } from './Meetings';

/** What the page edits — the booking page less what only the API knows. */
interface Draft {
  enabled: boolean;
  title: string;
  intro: string;
  durationMinutes: number;
  bufferMinutes: number;
  noticeMinutes: number;
  daysAhead: number;
  place: MeetingPlace;
  location: string;
  /** Day by day (0 Sunday … 6 Saturday), each stretch as the time fields keep it. */
  days: Array<Array<{ from: string; to: string }>>;
}

const LINK = /^https?:\/\/[^\s/$.?#][^\s]*$/i;

function draftOf(page: BookingPage): Draft {
  const days: Draft['days'] = Array.from({ length: 7 }, () => []);
  for (const w of page.windows) days[w.weekday].push({ from: minuteToTime(w.startMinute), to: minuteToTime(w.endMinute) });
  return {
    enabled: page.enabled,
    title: page.title ?? '',
    intro: page.intro ?? '',
    durationMinutes: page.durationMinutes,
    bufferMinutes: page.bufferMinutes,
    noticeMinutes: page.noticeMinutes,
    daysAhead: page.daysAhead,
    place: page.place,
    location: page.location ?? '',
    days,
  };
}

/** The draft's hours as the API takes them; a stretch with a time not filled in is left out. */
function windowsOf(draft: Draft): AvailabilityWindow[] {
  return draft.days.flatMap((stretches, weekday) =>
    stretches.flatMap((s) => {
      const startMinute = timeToMinute(s.from);
      const endMinute = timeToMinute(s.to, true);
      return startMinute === null || endMinute === null ? [] : [{ weekday, startMinute, endMinute }];
    }),
  );
}

const HOURS_PROBLEM: Record<'ORDER' | 'TOO_SHORT' | 'OVERLAP', TranslationKey> = {
  ORDER: 'booking.hoursOrder',
  TOO_SHORT: 'booking.hoursShort',
  OVERLAP: 'booking.hoursOverlap',
};

/**
 * Route: `/meetings/booking` — Meeting Calendar › Booking link (operator,
 * 2026-11-05: "create a page with public link to sent to anyone to choose meeting
 * also add setting to calendar to let any employee to add which time accept
 * meetings"). The signed-in person's booking page: its link, switched on or off;
 * the hours they take meetings, day by day in the company's time; and how long the
 * meetings are, where, and how soon and how far ahead they may be booked.
 */
export function MeetingBookingPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const weekStart = useWeekStart();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [askNewLink, setAskNewLink] = useState(false);

  const page = useQuery({
    queryKey: ['meetings', 'booking'],
    queryFn: () => api.get<BookingPage>('/meeting-booking/mine'),
  });

  // The draft starts from what is saved, and starts again from it after each save.
  useEffect(() => {
    if (page.data) setDraft(draftOf(page.data));
  }, [page.data]);

  const saved = useMemo(() => (page.data ? JSON.stringify(draftOf(page.data)) : ''), [page.data]);
  const dirty = !!draft && JSON.stringify(draft) !== saved;

  const save = useMutation({
    mutationFn: (d: Draft) =>
      api.put<BookingPage>('/meeting-booking/mine', {
        enabled: d.enabled,
        title: d.title.trim() || null,
        intro: d.intro.trim() || null,
        durationMinutes: d.durationMinutes,
        bufferMinutes: d.bufferMinutes,
        noticeMinutes: d.noticeMinutes,
        daysAhead: d.daysAhead,
        place: d.place,
        location: d.location.trim() || null,
        windows: windowsOf(d),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['meetings', 'booking'], next);
      toast.success(t('booking.saved'));
    },
    onError: (e) => toast.error(bookingRefusal(e, t)),
  });

  const newLink = useMutation({
    mutationFn: () => api.post<BookingPage>('/meeting-booking/mine/new-link', {}),
    onSuccess: (next) => {
      // Only the link: changes not saved yet stay in the draft.
      queryClient.setQueryData<BookingPage>(['meetings', 'booking'], (old) => (old ? { ...old, url: next.url } : next));
      setAskNewLink(false);
      toast.success(t('booking.newLinkToast'));
    },
    onError: (e) => toast.error(bookingRefusal(e, t)),
  });

  if (page.isLoading || (page.data && !draft)) return <LoadingBlock />;
  if (page.error || !page.data || !draft) return <ErrorState error={page.error} onRetry={() => void page.refetch()} />;
  const data = page.data;

  const set = (changes: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...changes } : d));
  const setDay = (weekday: number, stretches: Draft['days'][number]) =>
    setDraft((d) => (d ? { ...d, days: d.days.map((s, i) => (i === weekday ? stretches : s)) } : d));

  // Each day's stretches, checked as the API checks them.
  const problems = draft.days.map((stretches) =>
    dayHoursProblems(
      stretches.map((s) => ({ weekday: 0, startMinute: timeToMinute(s.from) ?? 0, endMinute: timeToMinute(s.to, true) ?? 0 })),
      draft.durationMinutes,
    ),
  );
  const hoursWrong = problems.some((day) => day.some(Boolean));
  const noHours = windowsOf(draft).length === 0;
  const locationWrong =
    (draft.place === 'OUTSIDE' && !draft.location.trim()) || (draft.place === 'ONLINE' && !!draft.location.trim() && !LINK.test(draft.location.trim()));
  const blocked = hoursWrong || locationWrong || (draft.enabled && noHours);
  const order = Array.from({ length: 7 }, (_, i) => (weekStart + i) % 7);
  const defaultTitle = data.defaultTitle[locale];

  const copyLink = () => {
    const done = () => toast.success(t('booking.copied'));
    void navigator.clipboard?.writeText(data.url).then(done, done);
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-4">
        <Link to="/meetings" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('meetings.title')}
        </Link>
      </div>

      <PageHeader title={t('booking.title')} subtitle={t('booking.subtitle')} />

      {!data.linksOn && (
        <div className="mb-5 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/8 px-4 py-2.5 text-body-sm text-[var(--text-primary)]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-fg dark:text-warning-dark" aria-hidden />
          {t('booking.offEverywhere')}
        </div>
      )}

      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!blocked && !save.isPending) save.mutate(draft);
        }}
      >
        <Card>
          <CardHeader
            title={
              <span className="inline-flex items-center gap-2">
                <Link2 className="h-5 w-5 text-[var(--text-tertiary)]" aria-hidden />
                {t('booking.linkTitle')}
              </span>
            }
            subtitle={t('booking.linkHint')}
          />
          <div className="space-y-4">
            <div>
              <Checkbox
                checked={draft.enabled}
                onChange={(e) => set({ enabled: e.target.checked })}
                label={<span className="font-medium text-[var(--text-primary)]">{t('booking.enabled')}</span>}
              />
              <p className="mt-1 ps-6 text-caption text-faint">{t('booking.enabledHint')}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                readOnly
                value={data.url}
                dir="ltr"
                aria-label={t('booking.linkTitle')}
                className="min-w-0 flex-1 font-mono text-caption"
                onFocus={(e) => e.target.select()}
              />
              <Button icon={<Copy className="h-4 w-4" aria-hidden />} onClick={copyLink}>
                {t('booking.copy')}
              </Button>
              <a href={data.url} target="_blank" rel="noopener noreferrer">
                <Button icon={<ExternalLink className="h-4 w-4" aria-hidden />}>{t('booking.open')}</Button>
              </a>
              <Button icon={<RefreshCw className="h-4 w-4" aria-hidden />} onClick={() => setAskNewLink(true)}>
                {t('booking.newLink')}
              </Button>
            </div>
            {data.enabled && !dirty && <p className="text-caption text-muted">{t('booking.upcoming', { n: data.upcoming })}</p>}
            {draft.enabled && noHours && <p className="text-caption text-error-fg dark:text-error-dark">{t('booking.noHours')}</p>}
          </div>
        </Card>

        <Card>
          <CardHeader title={t('booking.hoursTitle')} subtitle={t('booking.hoursHint', { timezone: data.timezone })} />
          <ul className="divide-y divide-[var(--line)]">
            {order.map((weekday) => {
              const stretches = draft.days[weekday];
              // A Sunday, for the weekday's name: 8 November 2026 + n.
              const name = formatWeekday(`2026-11-${String(8 + weekday).padStart(2, '0')}`, locale);
              return (
                <li key={weekday} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
                  <span className="w-32 shrink-0 pt-2 text-body-sm font-medium text-[var(--text-primary)]">{name}</span>
                  <div className="min-w-0 flex-1 space-y-2">
                    {stretches.length === 0 && <p className="pt-2 text-body-sm text-faint">{t('booking.dayOff')}</p>}
                    {stretches.map((s, i) => {
                      const problem = problems[weekday][i];
                      return (
                        <div key={i}>
                          <div className="flex items-center gap-2">
                            {/* The field fills its box: the box keeps it to a time's width, both on one row. */}
                            <div className="w-36 shrink-0">
                              <Input
                                type="time"
                                step={300}
                                value={s.from}
                                aria-label={`${name} · ${t('booking.from')}`}
                                className={clsx('tabular', problem && 'border-error')}
                                onChange={(e) => setDay(weekday, stretches.map((x, k) => (k === i ? { ...x, from: e.target.value } : x)))}
                              />
                            </div>
                            <span className="text-muted" aria-hidden>
                              –
                            </span>
                            <div className="w-36 shrink-0">
                              <Input
                                type="time"
                                step={300}
                                value={s.to}
                                aria-label={`${name} · ${t('booking.to')}`}
                                className={clsx('tabular', problem && 'border-error')}
                                onChange={(e) => setDay(weekday, stretches.map((x, k) => (k === i ? { ...x, to: e.target.value } : x)))}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => setDay(weekday, stretches.filter((_, k) => k !== i))}
                              aria-label={t('booking.removeHours')}
                              title={t('booking.removeHours')}
                              className="rounded-sm p-2 text-[var(--text-tertiary)] hover:bg-[var(--surface-sunken)] hover:text-error-fg dark:hover:text-error-dark"
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </button>
                          </div>
                          {problem && (
                            <p className="mt-1 text-caption text-error-fg dark:text-error-dark">
                              {t(HOURS_PROBLEM[problem], { time: bookingTimeText(draft.durationMinutes, locale) })}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1 sm:pt-1">
                    {stretches.length < MAX_BOOKING_WINDOWS_A_DAY && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Plus className="h-3.5 w-3.5" aria-hidden />}
                        onClick={() => setDay(weekday, [...stretches, nextStretch(stretches)])}
                      >
                        {t('booking.addHours')}
                      </Button>
                    )}
                    {stretches.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<CopyPlus className="h-3.5 w-3.5" aria-hidden />}
                        onClick={() => {
                          set({ days: draft.days.map(() => stretches.map((x) => ({ ...x }))) });
                          toast.success(t('booking.copiedToAll'));
                        }}
                      >
                        {t('booking.copyToAll')}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>

        <Card>
          <CardHeader title={t('booking.meetingTitle')} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('booking.pageTitle')} hint={t('booking.pageTitleHint', { title: defaultTitle })} className="sm:col-span-2">
              <Input
                value={draft.title}
                maxLength={MAX_BOOKING_TITLE}
                placeholder={defaultTitle}
                dir="auto"
                onChange={(e) => set({ title: e.target.value })}
              />
            </Field>
            <Field label={t('booking.intro')} hint={t('booking.introHint')} className="sm:col-span-2">
              <Textarea value={draft.intro} maxLength={MAX_BOOKING_INTRO} dir="auto" onChange={(e) => set({ intro: e.target.value })} />
            </Field>
            <Field label={t('booking.duration')}>
              <Select value={draft.durationMinutes} onChange={(e) => set({ durationMinutes: Number(e.target.value) })}>
                {BOOKING_DURATIONS.map((m) => (
                  <option key={m} value={m}>
                    {bookingTimeText(m, locale)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('booking.buffer')} hint={t('booking.bufferHint')}>
              <Select value={draft.bufferMinutes} onChange={(e) => set({ bufferMinutes: Number(e.target.value) })}>
                {BOOKING_BUFFERS.map((m) => (
                  <option key={m} value={m}>
                    {m === 0 ? t('booking.none') : bookingTimeText(m, locale)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('booking.notice')} hint={t('booking.noticeHint')}>
              <Select value={draft.noticeMinutes} onChange={(e) => set({ noticeMinutes: Number(e.target.value) })}>
                {withValue(BOOKING_NOTICES, draft.noticeMinutes).map((m) => (
                  <option key={m} value={m}>
                    {m === 0 ? t('booking.noticeNone') : t('booking.noticeBefore', { time: bookingTimeText(m, locale) })}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('booking.daysAhead')}>
              <Select value={draft.daysAhead} onChange={(e) => set({ daysAhead: Number(e.target.value) })}>
                {withValue(BOOKING_DAYS_AHEAD, draft.daysAhead).map((d) => (
                  <option key={d} value={d}>
                    {t('booking.daysAheadValue', { days: bookingDaysText(d, locale) })}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('booking.place')}>
              <Select value={draft.place} onChange={(e) => set({ place: e.target.value as MeetingPlace })}>
                {MEETING_PLACES.map((p) => (
                  <option key={p} value={p}>
                    {t(`meetings.place.${p}` as TranslationKey)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={t(`meetings.locationLabel.${draft.place}` as TranslationKey)}
              hint={t('booking.locationHint')}
              error={
                locationWrong
                  ? t(draft.place === 'OUTSIDE' ? 'meetings.locationRequired' : 'meetings.linkInvalid')
                  : null
              }
            >
              <Input
                value={draft.location}
                maxLength={MAX_MEETING_LOCATION}
                dir={draft.place === 'ONLINE' ? 'ltr' : 'auto'}
                placeholder={draft.place === 'ONLINE' ? 'https://' : undefined}
                onChange={(e) => set({ location: e.target.value })}
              />
            </Field>
          </div>
        </Card>

        <div
          data-form-actions
          className="sticky bottom-0 z-sticky -mx-4 border-t hairline bg-[var(--surface-page)]/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6"
        >
          <div className="flex flex-wrap items-center justify-end gap-3">
            {dirty && <span className="me-auto text-caption text-muted">{t('booking.unsaved')}</span>}
            <Button type="button" disabled={!dirty || save.isPending} onClick={() => setDraft(draftOf(data))}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty || blocked}>
              {t('booking.save')}
            </Button>
          </div>
        </div>
      </form>

      {askNewLink && (
        <ConfirmDialog
          title={t('booking.newLinkTitle')}
          confirmLabel={t('booking.newLink')}
          danger
          busy={newLink.isPending}
          onConfirm={() => newLink.mutate()}
          onCancel={() => setAskNewLink(false)}
        >
          <p>{t('booking.newLinkBody')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/** A new stretch after the day's last: an hour after it ends, two hours long; 09:00–17:00 on an empty day. */
function nextStretch(stretches: Array<{ from: string; to: string }>): { from: string; to: string } {
  const last = stretches.reduce((end, s) => Math.max(end, timeToMinute(s.to, true) ?? 0), 0);
  if (stretches.length === 0 || last === 0) return { from: '09:00', to: '17:00' };
  const start = Math.min(last + 60, 22 * 60);
  return { from: minuteToTime(start), to: minuteToTime(Math.min(start + 120, 1440)) };
}

/** The choices, with the one saved among them even when it is not one of them. */
function withValue(values: number[], value: number): number[] {
  return values.includes(value) ? values : [...values, value].sort((a, b) => a - b);
}

/** Why a save was refused, in words. */
function bookingRefusal(error: unknown, t: (key: TranslationKey, vars?: Record<string, string | number>) => string): string {
  const reason = (error as { details?: { reason?: string } } | null)?.details?.reason;
  if (reason === 'NO_HOURS') return t('booking.noHours');
  return refusalText(error, t) || t('booking.saveFailed');
}
